/**
 * PdfPreview
 *
 * Renderiza un PDF a partir de una blob URL. En desktop usa <iframe> que el
 * navegador renderiza con su visor nativo. En móvil (Chrome Android / Safari
 * iOS) los iframes de blob: PDF normalmente quedan en blanco — por eso
 * mostramos un panel con botón para abrir el PDF en pestaña nueva (donde sí
 * funciona) y otro para descargar.
 */

import { ExternalLink, Download, Eye, Loader2 } from "lucide-react";
import Button from "@/components/ui/Button";

export interface PdfPreviewProps {
  /** Blob URL del PDF. Si es null se muestra un spinner. */
  url: string | null;
  /** Nombre sugerido de descarga */
  fileName?: string;
  /** Alto del iframe en desktop. Default 60vh. */
  desktopHeight?: string;
  /** Texto de ayuda mostrado bajo los botones en móvil */
  mobileHint?: string;
}

function isMobileDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  if (/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) return true;
  if (
    typeof window !== "undefined" &&
    "ontouchstart" in window &&
    navigator.maxTouchPoints > 0
  ) {
    return true;
  }
  return false;
}

export function PdfPreview({
  url,
  fileName = "documento.pdf",
  desktopHeight = "60vh",
  mobileHint = "Pulsa “Abrir en pestaña nueva” para revisar el PDF. El visor integrado no funciona en móviles.",
}: PdfPreviewProps) {
  if (!url) {
    return (
      <div
        className="flex items-center justify-center text-gray-500 rounded-lg border border-gray-300 bg-white"
        style={{ height: desktopHeight }}
      >
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  const mobile = isMobileDevice();

  if (mobile) {
    return (
      <div className="rounded-lg border border-purple-200 bg-gradient-to-br from-purple-50 to-white p-5 flex flex-col items-center text-center">
        <div className="w-16 h-16 rounded-2xl bg-purple-100 border border-purple-200 flex items-center justify-center mb-3">
          <Eye className="w-8 h-8 text-purple-600" />
        </div>
        <p className="text-sm font-semibold text-gray-900 mb-1">
          Pulsa para previsualizar el PDF
        </p>
        <p className="text-xs text-gray-500 mb-4 max-w-sm">{mobileHint}</p>
        <div className="flex flex-col sm:flex-row gap-2 w-full max-w-sm">
          <Button
            type="button"
            variant="primary"
            className="flex-1 py-3 text-sm font-semibold"
            onClick={() => {
              const w = window.open(url, "_blank", "noopener,noreferrer");
              // Algunos Safari iOS bloquean window.open desde handler indirecto;
              // si falló, usamos un anchor temporal como fallback.
              if (!w) {
                const a = document.createElement("a");
                a.href = url;
                a.target = "_blank";
                a.rel = "noopener noreferrer";
                document.body.appendChild(a);
                a.click();
                a.remove();
              }
            }}
          >
            <ExternalLink className="w-4 h-4 mr-2 inline" />
            Abrir en pestaña nueva
          </Button>
          <Button
            type="button"
            variant="outline"
            className="flex-1 py-3 text-sm"
            onClick={() => {
              const a = document.createElement("a");
              a.href = url;
              a.download = fileName;
              document.body.appendChild(a);
              a.click();
              a.remove();
            }}
          >
            <Download className="w-4 h-4 mr-2 inline" />
            Descargar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <iframe
      src={url}
      title="Vista previa del documento"
      className="w-full rounded-lg border border-gray-300 bg-white"
      style={{ height: desktopHeight }}
    />
  );
}

export default PdfPreview;
